import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { cn } from "../utils";

const MONTH_LABELS = [
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez",
] as const;

export const MONTH_FULL_LABELS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
] as const;

/** "2026-08" → { year: 2026, month: 8 } (month is 1-based). */
function parseYearMonth(value: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) };
}

function toYearMonth(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

export function formatYearMonth(value: string): string {
  const parsed = parseYearMonth(value);
  if (!parsed) return value;
  return `${MONTH_FULL_LABELS[parsed.month - 1]} ${parsed.year}`;
}

/**
 * Inline month-of-year grid with a year stepper. Value is "YYYY-MM".
 * Compose inside a Popover for a picker, or render inline in panels.
 */
export function MonthGrid({
  value,
  onValueChange,
  min,
  max,
  className,
}: {
  value: string;
  onValueChange: (value: string) => void;
  /** Inclusive "YYYY-MM" bounds. */
  min?: string;
  max?: string;
  className?: string;
}) {
  const selected = parseYearMonth(value);
  const [year, setYear] = useState(
    () => selected?.year ?? new Date().getFullYear(),
  );

  const minParsed = min ? parseYearMonth(min) : null;
  const maxParsed = max ? parseYearMonth(max) : null;

  const isDisabled = (month: number) => {
    if (minParsed && (year < minParsed.year || (year === minParsed.year && month < minParsed.month))) {
      return true;
    }
    if (maxParsed && (year > maxParsed.year || (year === maxParsed.year && month > maxParsed.month))) {
      return true;
    }
    return false;
  };

  const canGoBack = !minParsed || year > minParsed.year;
  const canGoForward = !maxParsed || year < maxParsed.year;

  return (
    <div className={cn("w-52", className)}>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          disabled={!canGoBack}
          onClick={() => setYear((y) => y - 1)}
          className={cn(
            "flex size-6 items-center justify-center rounded-control text-ink-muted",
            "transition-colors duration-100 hover:bg-surface-sunken hover:text-ink",
            "active:scale-95 disabled:pointer-events-none disabled:opacity-40",
          )}
          aria-label="Ano anterior"
        >
          <ChevronLeft className="size-3.5" />
        </button>
        <span className="text-[0.8125rem] font-medium tabular-nums text-ink">
          {year}
        </span>
        <button
          type="button"
          disabled={!canGoForward}
          onClick={() => setYear((y) => y + 1)}
          className={cn(
            "flex size-6 items-center justify-center rounded-control text-ink-muted",
            "transition-colors duration-100 hover:bg-surface-sunken hover:text-ink",
            "active:scale-95 disabled:pointer-events-none disabled:opacity-40",
          )}
          aria-label="Próximo ano"
        >
          <ChevronRight className="size-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {MONTH_LABELS.map((label, index) => {
          const month = index + 1;
          const isSelected =
            selected?.year === year && selected.month === month;
          return (
            <button
              key={label}
              type="button"
              disabled={isDisabled(month)}
              onClick={() => onValueChange(toYearMonth(year, month))}
              className={cn(
                "h-7 rounded-control text-2xs font-medium capitalize",
                "transition-[background-color,color,transform] duration-100 ease-swift",
                "active:scale-95 disabled:pointer-events-none disabled:opacity-40",
                isSelected
                  ? "bg-ember text-ink-inverse"
                  : "text-ink-secondary hover:bg-surface-sunken hover:text-ink",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
